(() => {
  const root = () => window.Shopify?.routes?.root || '/';

  function publishEvent(name, data) {
    if (
      typeof publish === 'function' &&
      typeof PUB_SUB_EVENTS !== 'undefined' &&
      PUB_SUB_EVENTS[name]
    ) {
      publish(PUB_SUB_EVENTS[name], data);
    }
  }

  async function addToCart(formData) {
    // De native theme-drawer wordt niet gebruikt.
    formData.delete('sections');
    formData.delete('sections_url');

    const response = await fetch(root() + 'cart/add.js', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: formData,
    });

    const result = await response.json();

    if (!response.ok || result.status) {
      throw new Error(
        result.description ||
        result.message ||
        'Het product kon niet worden toegevoegd.'
      );
    }

    return result;
  }

  async function openOpus() {
    const deadline = Date.now() + 10000;

    while (
      typeof window.opusOpen !== 'function' ||
      typeof window.opusRefreshCart !== 'function'
    ) {
      if (Date.now() >= deadline) {
        throw new Error('De Opus-winkelwagen kon niet worden geopend.');
      }

      await new Promise(resolve => setTimeout(resolve, 100));
    }

    await window.opusRefreshCart();
    window.opusOpen();
  }

  /*
   * Onderschep klikken op /cart/VARIANT:AANTAL-links.
   * Voeg toe zonder navigatie en open alleen Opus.
   */
  if (!window.kkCartLinkHandlerInstalled) {
    window.kkCartLinkHandlerInstalled = true;

    let linkBusy = false;

    document.addEventListener('click', async event => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.ctrlKey ||
        event.metaKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      const link = event.target.closest?.('a[href]');
      if (!link || link.hasAttribute('download')) return;
      if (link.target && link.target !== '_self') return;

      const url = new URL(link.href, window.location.href);
      if (url.origin !== window.location.origin) return;

      const match = url.pathname.match(/\/cart\/(\d+):(\d+)\/?$/);
      if (!match) return;

      const quantity = Number(match[2]);
      if (!Number.isSafeInteger(quantity) || quantity < 1) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      if (linkBusy) return;

      linkBusy = true;
      link.setAttribute('aria-busy', 'true');
      let added = false;

      try {
        const formData = new FormData();
        formData.set('id', match[1]);
        formData.set('quantity', String(quantity));

        await addToCart(formData);
        added = true;

        await openOpus();
      } catch (error) {
        console.error('[Koffie Kenner cart-link]', error);

        alert(
          added
            ? 'Het product is toegevoegd, maar de winkelwagen kon niet worden geopend.'
            : error.message
        );
      } finally {
        linkBusy = false;
        link.removeAttribute('aria-busy');
      }
    }, true);
  }

  if (customElements.get('product-form')) return;

  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        if (!this.form) return;

        if (this.variantIdInput) {
          this.variantIdInput.disabled = false;
        }

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.isSubmitting = false;
        this.refreshSubmitButton();

        this.form.addEventListener(
          'submit',
          this.onSubmitHandler.bind(this)
        );
      }

      async onSubmitHandler(event) {
        // Als Opus de submit al afhandelt, niet dubbel toevoegen.
        if (event.defaultPrevented) return;

        event.preventDefault();

        if (
          !this.submitButton ||
          this.isSubmitting ||
          this.submitButton.disabled ||
          this.submitButton.getAttribute('aria-disabled') === 'true'
        ) {
          return;
        }

        this.isSubmitting = true;
        this.handleErrorMessage();

        const button = this.submitButton;
        const spinner = this.querySelector('.loading__spinner');

        button.setAttribute('aria-disabled', 'true');
        button.classList.add('loading');
        spinner?.classList.remove('hidden');

        const formData = new FormData(this.form);
        let added = false;

        try {
          const result = await addToCart(formData);
          added = true;

          publishEvent('cartUpdate', {
            source: 'product-form',
            productVariantId: formData.get('id'),
            cartData: result,
          });

          const modal = this.closest('quick-add-modal');

          if (modal && typeof modal.hide === 'function') {
            await new Promise(resolve => {
              let finished = false;
              let timer;

              const finish = () => {
                if (finished) return;
                finished = true;

                clearTimeout(timer);
                document.body.removeEventListener('modalClosed', finish);
                resolve();
              };

              document.body.addEventListener(
                'modalClosed',
                finish,
                { once: true }
              );

              timer = setTimeout(finish, 1000);
              modal.hide(true);
            });
          }

          await openOpus();
        } catch (error) {
          console.error('[Koffie Kenner product-form]', error);

          const message = added
            ? 'Toegevoegd aan je winkelwagen, maar de winkelwagen kon niet worden geopend.'
            : error.message;

          if (!added) {
            publishEvent('cartError', {
              source: 'product-form',
              productVariantId: formData.get('id'),
              errors: message,
              message,
            });
          }

          this.handleErrorMessage(message);
        } finally {
          this.isSubmitting = false;
          button.classList.remove('loading');
          button.removeAttribute('aria-disabled');
          spinner?.classList.add('hidden');
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector('.product-form__error-message-wrapper');

        if (!this.errorMessageWrapper) return;

        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            '.product-form__error-message'
          );

        this.errorMessageWrapper.toggleAttribute(
          'hidden',
          !errorMessage
        );

        if (errorMessage && this.errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (!this.submitButton) return;

        this.submitButton.disabled = disable;

        if (this.submitButtonText) {
          if (disable && text) {
            this.submitButtonText.textContent = text;
          } else if (!disable) {
            this.submitButtonText.textContent =
              window.variantStrings?.addToCart ||
              'Toevoegen aan winkelwagen';
          }
        }
      }

      refreshSubmitButton() {
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText =
          this.submitButton?.querySelector('span');

        this.submitButton?.setAttribute('aria-haspopup', 'dialog');
      }

      get variantIdInput() {
        return this.form?.querySelector('[name="id"]');
      }
    }
  );
})();

